import { CloseCircleFilled, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useContext, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import {
  type ModelExpenseArticle,
  useLazyGetExpenseArticlesQuery,
  useUpdateExpenseArticleMutation,
} from '@app/api/ExpenseArticles';
import { useAddMoneyExpenseMutation } from '@app/api/MoneyExpenses';
import type { ModelPaymentAccount } from '@app/api/PaymentAccounts';
import {
  CloseIconsForModalWithDataTestId,
  CustomInputNumber,
  EmptyComponentWithDataTestId,
  UploadFilesComponent,
} from '@app/components';
import {
  DATE_PICKER_FORMAT,
  EMPTY_INPUT_VALUE_VALIDATOR,
  ERROR_REQUIRED_FIELD,
  EXPENSES_MODAL_FILES_EXTENSIONS,
  ADD_PAYMENT_ACCOUNT_PATH,
  FRONT_WARNING,
  PREFIX,
  CASH,
  SUCCESS,
} from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import { selectAccessRights, useAppSelector } from '@app/store';
import { addDataTestIdsToSelectDropdown, divideAmount } from '@app/utils';

import type { NClientPaymentsList } from '@pages/Clients/ClientPayments/ClientPaymentsList/ClientPaymentsList.types';

import type { FormInstance, InputRef } from 'antd';
import type { FC } from 'react';

import '@pages/Clients/ClientPayments/ClientPaymentsList/components/AddingModal.desktop.scss';

const { TextArea } = Input;
const { Text, Title } = Typography;

const noZeroPattern = /^(?!0$|0\.0{1,2}$)-?\d+(\.\d{1,2})?$/;

type AddingModalProps = {
  title: string;
  isModalOpen: boolean;
  setIsModalOpen: (arg: boolean) => void;
  form: FormInstance;
  paymentAccounts: ModelPaymentAccount[];
  className: string;
  isExpense: boolean;
  isCollection: boolean;
  submit?: NClientPaymentsList.GeneralFunctionsResult;
  setSelectedPaymentAccountId: (value: number) => void;
  initialSearch: () => Promise<void>;
  isModalLoading: boolean;
  selectedPaymentAccountId: number;
  titleDataTestId?: string;
  setIsModalLoading: React.Dispatch<React.SetStateAction<boolean>>;
  getPaymentAccountsForSystemHandler: () => Promise<void>;
};

export const AddingModal: FC<AddingModalProps> = ({
  title,
  isModalOpen,
  setIsModalOpen,
  form,
  paymentAccounts,
  className,
  isExpense,
  isCollection,
  submit,
  setSelectedPaymentAccountId,
  initialSearch,
  isModalLoading,
  selectedPaymentAccountId,
  titleDataTestId = '',
  setIsModalLoading,
  getPaymentAccountsForSystemHandler,
}) => {
  const { showNotification } = useContext(NotificationContext);
  const [addMoneyExpense] = useAddMoneyExpenseMutation();
  const [getExpenseArticles] = useLazyGetExpenseArticlesQuery();
  const [updateExpenseArticle] = useUpdateExpenseArticleMutation();
  const accessRights = useAppSelector(selectAccessRights);
  const isAdmin = accessRights?.IsAdmin;

  const [newArticleName, setNewArticleName] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const newArticleInputRef = useRef<InputRef>(null);
  const { activeBranchId } = useUserPrint();
  const [localExpenseArticles, setLocalExpenseArticles] = useState<
    ModelExpenseArticle[]
  >([]);

  useEffect(() => {
    const fetchExpenseArticles = async () => {
      if (isModalOpen && isExpense) {
        try {
          const articles = await getExpenseArticles({
            isOnlyActive: true,
            ActiveBranchId: activeBranchId,
          }).unwrap();

          setLocalExpenseArticles(articles);
        } catch (error) {
          showNotification(handleCatchError(error));
        }
      }
    };

    fetchExpenseArticles();
  }, [
    isModalOpen,
    isExpense,
    activeBranchId,
    getExpenseArticles,
    showNotification,
  ]);

  const handleCreateArticle = async () => {
    const hasNewArticleName = newArticleName.trim();

    if (!hasNewArticleName) {
      return;
    }

    const hasDuplicateArticle = localExpenseArticles.some(
      ({ Name }) => Name?.toLowerCase() === hasNewArticleName.toLowerCase()
    );

    if (hasDuplicateArticle) {
      showNotification(
        'Статья расходов с таким названием уже существует',
        FRONT_WARNING
      );
      return;
    }

    setLoading(true);

    try {
      await updateExpenseArticle({
        ActiveBranchId: activeBranchId,
        data: {
          Name: hasNewArticleName,
          IsDeleted: false,
          Description: '',
        },
      }).unwrap();

      const updated = await getExpenseArticles({
        isOnlyActive: true,
        ActiveBranchId: activeBranchId,
      }).unwrap();

      setLocalExpenseArticles(updated);

      const created = updated.find(({ Name }) => Name === hasNewArticleName);

      if (created) {
        form.setFieldValue('Expenses', created.Id);
        setNewArticleName('');
        setIsSelectOpen(false);
        showNotification('Статья расходов добавлена успешно', SUCCESS);
      } else if (updated.length > 0) {
        form.setFieldValue('Expenses', updated[0].Id);
        setNewArticleName('');
        setIsSelectOpen(false);
      }
    } catch (error) {
      showNotification(handleCatchError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleOk = async () => {
    if (isExpense) {
      const {
        Amount,
        PaymentAccounts,
        Expenses,
        Description,
        TimeCreated,
        Files,
      } = form.getFieldsValue();

      if (Amount && PaymentAccounts && Expenses) {
        try {
          const formData = new FormData();

          if (Files?.length) {
            const filesList = Files as { originFileObj: File }[];
            const base64Files = await Promise.all(
              filesList.map(({ originFileObj }) => originFileObj)
            );
            base64Files.forEach((base64) => {
              formData.append('attachments', base64);
            });
          }

          setIsModalLoading(true);

          await addMoneyExpense({
            ActiveBranchId: activeBranchId,
            data: formData,
            PaymentAccountId: PaymentAccounts,
            ExpenseArticleId: Expenses,
            Amount,
            Comment: Description || '',
            TimeCreated: TimeCreated
              ? dayjs(TimeCreated).format('YYYY-MM-DDTHH:mm:ss')
              : undefined,
          }).unwrap();

          showNotification('', SUCCESS);
          await initialSearch();
          setIsModalOpen(false);
          setIsModalLoading(false);
          form.resetFields();
          setSelectedPaymentAccountId(0);
          getPaymentAccountsForSystemHandler();
        } catch (error) {
          showNotification(handleCatchError(error));
        }
      }
    } else {
      const values = form.getFieldsValue();
      const { Amount, PaymentAccounts } = values;

      if (Amount && PaymentAccounts) {
        try {
          if (submit) {
            await submit(values);
          }

          await initialSearch();
          setIsModalOpen(false);
          form.resetFields();
          setSelectedPaymentAccountId(0);
          getPaymentAccountsForSystemHandler();
        } catch (error) {
          showNotification(handleCatchError(error));
        }
      }
    }
  };

  const renderNewArticleInput = () => {
    return (
      <div className={`${PREFIX}new-article-input`}>
        <div className={`${PREFIX}new-article-input__container`}>
          <Input
            ref={newArticleInputRef}
            value={newArticleName}
            onChange={({ target: { value } }) => setNewArticleName(value)}
            placeholder="Введите название"
            disabled={loading}
            onPressEnter={handleCreateArticle}
            size="small"
            data-testid="expense-category-input"
            maxLength={200}
          />
          <Button
            type="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleCreateArticle();
            }}
            loading={loading}
            disabled={!newArticleName.trim()}
            icon={<PlusOutlined />}
            size="small"
            data-testid="create-expense-category"
          >
            Создать
          </Button>
        </div>
      </div>
    );
  };

  const renderDropdownMenu = (menu: React.ReactNode) => {
    return (
      <div>
        {isAdmin && renderNewArticleInput()}
        <div className={`${PREFIX}dropdown-menu`}>{menu}</div>
      </div>
    );
  };

  const expenseArticleOptions = localExpenseArticles.map(({ Id, Name }) => ({
    value: Id,
    label: Name || '',
  }));

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ margin: '0' }}>
            {generateSpanWithDataTestId(title, titleDataTestId)}
            <br />
            {isExpense || isCollection ? (
              <Text
                type="secondary"
                style={{ marginTop: '-10px' }}
                data-testid={`description-${
                  isExpense ? 'expense' : 'encashment'
                }`}
              >
                При создании {isExpense ? 'расхода' : 'инкассации'} сумма
                спишется с баланса кассы
              </Text>
            ) : (
              ''
            )}
          </Title>
        </div>
      }
      closeIcon={<CloseIconsForModalWithDataTestId />}
      open={isModalOpen}
      onCancel={() => {
        setIsModalOpen(false);
        form.resetFields();
        setSelectedPaymentAccountId(0);
      }}
      okText="Добавить"
      className={className}
      onOk={form.submit}
      okButtonProps={{
        loading: isModalLoading,
        'data-testid': 'add',
      }}
      cancelButtonProps={{
        disabled: isModalLoading,
        'data-testid': 'cancel',
      }}
    >
      <Form form={form} onFinish={handleOk} layout="vertical">
        <Space size="large" direction="vertical">
          <div className="container">
            <Text className="label-require" strong data-testid="amount-title">
              Сумма
            </Text>

            <Form.Item
              name="Amount"
              className="mg-0"
              rules={[
                {
                  required: true,
                  message: ERROR_REQUIRED_FIELD,
                },
                {
                  pattern: noZeroPattern,
                  message: 'Минимальная сумма: 0,01 ₽',
                },
              ]}
            >
              <CustomInputNumber
                className="custom-amount"
                isAllowed={(floatValue) => floatValue >= 0.01}
                onSuccess={({ value }) => {
                  form.setFieldValue('Amount', value.replace(/\.$/, ''));
                }}
                dataTestId="amount-input"
              />
            </Form.Item>
          </div>

          <div className="container">
            <Text className="label-require" strong data-testid="kassa-title">
              Касса
            </Text>

            <Form.Item
              name="PaymentAccounts"
              rules={[
                {
                  required: true,
                  message: ERROR_REQUIRED_FIELD,
                },
              ]}
              className="mg-0"
            >
              <Select
                data-testid="kassa-select"
                options={paymentAccounts.map((item) => {
                  return {
                    label: item.Name,
                    value: item.Id,
                  };
                })}
                size="large"
                onChange={(value) => {
                  setSelectedPaymentAccountId(value);
                }}
                notFoundContent={
                  <div className="empty-cashbox">
                    <Empty
                      description={
                        <div className="empty-cashbox description">
                          <span className="empty-cashbox description__text">
                            Нет доступных касс.{' '}
                          </span>
                          <Link
                            to={ADD_PAYMENT_ACCOUNT_PATH}
                            className="empty-cashbox description__link"
                          >
                            Создать кассу
                          </Link>
                        </div>
                      }
                    />
                  </div>
                }
                optionRender={(option) =>
                  addDataTestIdsToSelectDropdown({
                    form,
                    option,
                    itemDataTestId: 'kassa-name',
                    activeItemDataTestId: 'kassa-selected',
                    formItemName: 'PaymentAccounts',
                  })
                }
              />
            </Form.Item>
          </div>

          {selectedPaymentAccountId !== 0 ? (
            <div style={{ marginTop: '-20px' }}>
              {paymentAccounts.find(
                (item) => item.Id === selectedPaymentAccountId
              )?.Bank &&
              paymentAccounts.find(
                (item) => item.Id === selectedPaymentAccountId
              )?.PayType !== CASH ? (
                <Text style={{ color: 'rgb(0 0 0 / 55%)' }}>
                  <Text
                    strong
                    style={{ color: 'rgb(0 0 0 / 55%)' }}
                    data-testid="bank-title"
                  >
                    Банк:{' '}
                  </Text>
                  {generateSpanWithDataTestId(
                    paymentAccounts.find(
                      (item) => item.Id === selectedPaymentAccountId
                    )?.Bank,
                    'bank-name'
                  )}{' '}
                  <br />
                </Text>
              ) : (
                ''
              )}

              <Text style={{ color: 'rgb(0 0 0 / 55%)' }}>
                <Text
                  strong
                  style={{ color: 'rgb(0 0 0 / 55%)' }}
                  data-testid="balance-title"
                >
                  Баланс:{' '}
                </Text>
                {generateSpanWithDataTestId(
                  divideAmount(
                    paymentAccounts.find(
                      (item) => item.Id === selectedPaymentAccountId
                    )?.Balance || 0
                  ),
                  'balance-value'
                )}{' '}
                <br />
              </Text>

              {paymentAccounts.find(
                (item) => item.Id === selectedPaymentAccountId
              )?.Description ? (
                <Text style={{ color: 'rgb(0 0 0 / 55%)' }}>
                  <Text
                    strong
                    style={{ color: 'rgb(0 0 0 / 55%)' }}
                    data-testid="description-title"
                  >
                    Описание:{' '}
                  </Text>
                  {generateSpanWithDataTestId(
                    paymentAccounts.find(
                      (item) => item.Id === selectedPaymentAccountId
                    )?.Description,
                    'description-value'
                  )}{' '}
                  <br />
                </Text>
              ) : (
                ''
              )}
            </div>
          ) : (
            ''
          )}

          {isExpense && (
            <div className="container">
              <Text
                className="label-require"
                strong
                data-testid="expense-category-title"
              >
                Статья расходов
              </Text>
              {localExpenseArticles.length === 0 && !loading && isAdmin ? (
                <div className="empty-expense-article">
                  <div style={{ display: 'flex', width: '100%' }}>
                    <Form.Item
                      name="ExpenseCategory"
                      rules={[
                        {
                          required: true,
                          message: ERROR_REQUIRED_FIELD,
                        },
                      ]}
                      className="mg-0"
                    >
                      <Input
                        ref={newArticleInputRef}
                        value={newArticleName}
                        onChange={({ target: { value } }) =>
                          setNewArticleName(value)
                        }
                        placeholder="Введите название"
                        style={{ flex: 1 }}
                        disabled={loading}
                        onPressEnter={handleCreateArticle}
                        autoFocus
                        size="large"
                        data-testid="expense-category-input"
                        maxLength={200}
                      />
                    </Form.Item>
                    <Button
                      type="primary"
                      onClick={handleCreateArticle}
                      loading={loading}
                      disabled={!newArticleName.trim()}
                      icon={<PlusOutlined />}
                      style={{ marginLeft: 8 }}
                      size="large"
                      data-testid="create-expense-category"
                    >
                      Создать
                    </Button>
                  </div>
                </div>
              ) : (
                <Form.Item
                  name="Expenses"
                  className="mg-0"
                  rules={[
                    {
                      required: true,
                      message: ERROR_REQUIRED_FIELD,
                    },
                  ]}
                >
                  <Select
                    className="payments-expense-article-field"
                    data-testid="expense-category-select"
                    showSearch
                    placeholder="Выберите статью расхода"
                    optionFilterProp="label"
                    open={isSelectOpen}
                    onOpenChange={setIsSelectOpen}
                    options={expenseArticleOptions}
                    value={form.getFieldValue('Expenses')}
                    onChange={(value) => {
                      form.setFieldValue('Expenses', value);
                    }}
                    notFoundContent={<EmptyComponentWithDataTestId />}
                    popupRender={renderDropdownMenu}
                    optionRender={(option) =>
                      addDataTestIdsToSelectDropdown({
                        form,
                        option,
                        itemDataTestId: 'expense-category-name',
                        activeItemDataTestId: 'expense-category-selected',
                        formItemName: 'Expenses',
                      })
                    }
                  />
                </Form.Item>
              )}
            </div>
          )}

          <div className="container">
            <Text
              className={isCollection || isExpense ? 'label-require' : ''}
              strong
              data-testid="comment-title"
            >
              Комментарий
            </Text>

            <Form.Item
              name="Description"
              className="mg-0"
              rules={
                isCollection || isExpense
                  ? [
                      {
                        required: true,
                        message: ERROR_REQUIRED_FIELD,
                      },
                      EMPTY_INPUT_VALUE_VALIDATOR,
                    ]
                  : []
              }
            >
              <TextArea rows={4} maxLength={400} data-testid="comment-input" />
            </Form.Item>
          </div>

          {isExpense && (
            <div className="container">
              <Text strong data-testid="expense-date-title">
                Дата расхода
              </Text>

              <Form.Item name="TimeCreated" className="mg-0">
                <DatePicker
                  className="expense-date-field"
                  size="large"
                  format={DATE_PICKER_FORMAT[0]}
                  data-testid="expense-date-select"
                  allowClear={{
                    clearIcon: (
                      <CloseCircleFilled data-testid="expense-date-remove-icon" />
                    ),
                  }}
                />
              </Form.Item>
            </div>
          )}

          {isExpense && (
            <div className="container">
              <UploadFilesComponent
                formItemName={'Files'}
                uploadFilesExtensions={EXPENSES_MODAL_FILES_EXTENSIONS}
                buttonDataTestId="expense-file-upload"
                maxCount={15}
              />
            </div>
          )}
        </Space>
      </Form>
    </Modal>
  );
};
